import React from 'react';

import type {
  Application,
  DeckRuntimeServices,
  IModalComponentProps,
  IRouterInjectedProps,
  IStage,
} from '@spinnaker/core';
import {
  DeckRuntimeContext,
  noop,
  ReactModal,
  TaskMonitor,
  withRouter,
  WizardModal,
  WizardPage,
} from '@spinnaker/core';

import { WizardServerGroupBasicSettings } from './BasicSettings';
import { WizardServerGroupConfigFilesSettings } from './ConfigFiles';
import type { ICloudrunServerGroupCommandData } from '../serverGroupCommandBuilder.service';
import { CloudrunServerGroupCommandBuilder } from '../serverGroupCommandBuilder.service';

export interface ICloudrunServerGroupModalProps extends IModalComponentProps {
  title: string;
  application: Application;
  command: ICloudrunServerGroupCommandData;
  isNew?: boolean;
  closeModal?: (command: any) => void;
  dismissModal?: () => void;
}

export interface ICloudrunServerGroupModalState {
  command: ICloudrunServerGroupCommandData;
  loaded: boolean;
  taskMonitor: TaskMonitor;
}

export class ServerGroupWizardComponent extends React.Component<
  ICloudrunServerGroupModalProps & IRouterInjectedProps,
  ICloudrunServerGroupModalState
> {
  public static contextType = DeckRuntimeContext;
  public declare context: React.ContextType<typeof DeckRuntimeContext>;

  public static defaultProps: Partial<ICloudrunServerGroupModalProps & IRouterInjectedProps> = {
    closeModal: noop,
    dismissModal: noop,
  };

  private _isUnmounted = false;
  private applicationRefreshUnsubscribe?: () => void;

  /*     private serverGroupWriter: ServerGroupWriter; */
  public static show(
    props: ICloudrunServerGroupModalProps,
    runtimeServices: DeckRuntimeServices,
  ): Promise<ICloudrunServerGroupCommandData> {
    const modalProps = { dialogClassName: 'wizard-modal modal-lg' };
    return ReactModal.show(ServerGroupWizard, props, modalProps, runtimeServices);
  }

  constructor(props: ICloudrunServerGroupModalProps & IRouterInjectedProps) {
    super(props);
    if (!props.command) {
      CloudrunServerGroupCommandBuilder.buildNewServerGroupCommand(props.application, 'cloudrun', 'create').then(
        (command) => {
          Object.assign(this.state.command, command);
          this.setState({ loaded: true });
        },
      );
    }

    this.state = {
      loaded: !!props.command,
      command: props.command || ({} as ICloudrunServerGroupCommandData),
      taskMonitor: new TaskMonitor({
        application: props.application,
        title: `${
          props.command.command.viewState.submitButtonLabel === 'Create' ? 'Creating' : 'Updating'
        } your Server Group`,
        onDismiss: () => this.props.dismissModal(),
        onTaskComplete: this.onTaskComplete,
      }),
    };
  }

  private onTaskComplete = () => {
    this.clearApplicationRefreshSubscription();
    this.applicationRefreshUnsubscribe = this.props.application.serverGroups.onNextRefresh(this.onApplicationRefresh);
    this.props.application.serverGroups.refresh();
  };

  protected onApplicationRefresh = (): void => {
    this.clearApplicationRefreshSubscription();
    if (this._isUnmounted) {
      return;
    }

    const { command } = this.props;
    const { taskMonitor } = this.state;
    const cloneStage = taskMonitor.task.execution.stages.find((stage: IStage) => stage.type === 'cloneServerGroup');
    if (cloneStage && cloneStage.context['deploy.server.groups']) {
      const newServerGroupName = cloneStage.context['deploy.server.groups'][command.command.region];
      if (newServerGroupName) {
        const newStateParams = {
          serverGroup: newServerGroupName,
          accountId: command.command.credentials,
          region: command.command.region,
          provider: 'cloudrun',
        };
        let transitionTo = '^.^.^.clusters.serverGroup';
        if (this.props.stateService.includes('**.clusters.serverGroup')) {
          // clone via details, all view
          transitionTo = '^.serverGroup';
        }
        if (this.props.stateService.includes('**.clusters.cluster.serverGroup')) {
          // clone or create with details open
          transitionTo = '^.^.serverGroup';
        }
        if (this.props.stateService.includes('**.clusters')) {
          // create new, no details open
          transitionTo = '.serverGroup';
        }
        this.props.stateService.go(transitionTo, newStateParams);
      }
    }
  };

  public componentWillUnmount(): void {
    this._isUnmounted = true;
    this.clearApplicationRefreshSubscription();
  }

  private clearApplicationRefreshSubscription = (): void => {
    this.applicationRefreshUnsubscribe?.();
    this.applicationRefreshUnsubscribe = undefined;
  };

  private submit = (c: ICloudrunServerGroupCommandData): void => {
    const command: any = CloudrunServerGroupCommandBuilder.copyAndCleanCommand(c.command);
    const forPipelineConfig = command.viewState.mode === 'editPipeline' || command.viewState.mode === 'createPipeline';
    if (forPipelineConfig) {
      this.props.closeModal && this.props.closeModal(command);
    } else {
      //command.viewState.mode = 'create';
      const submitMethod = () =>
        this.context.services.serverGroupWriter.cloneServerGroup(command, this.props.application);
      this.state.taskMonitor.submit(submitMethod);
      return null;
    }
  };
  public render() {
    const { dismissModal, application } = this.props;
    const { loaded, taskMonitor, command } = this.state;
    const labelButton = this.state.command.command.viewState.submitButtonLabel;

    return (
      <WizardModal<ICloudrunServerGroupCommandData>
        heading={`${labelButton === 'Add' || labelButton === 'Create' ? 'Create New' : 'Update'} Server Group`}
        initialValues={command}
        loading={!loaded}
        taskMonitor={taskMonitor}
        dismissModal={dismissModal}
        closeModal={this.submit}
        submitButtonLabel={labelButton}
        render={({ formik, nextIdx, wizard }) => (
          <>
            <WizardPage
              label="Basic Settings"
              wizard={wizard}
              order={nextIdx()}
              render={({ innerRef }) => (
                <WizardServerGroupBasicSettings ref={innerRef} formik={formik} app={application} />
              )}
            />

            <WizardPage
              label="Service Yaml"
              wizard={wizard}
              order={nextIdx()}
              render={({ innerRef }) => <WizardServerGroupConfigFilesSettings ref={innerRef} formik={formik} />}
            />
          </>
        )}
      />
    );
  }
}

export const ServerGroupWizard = Object.assign(withRouter(ServerGroupWizardComponent), {
  show: ServerGroupWizardComponent.show,
});
